<template lang="pug">
yz-dialog(v-model="showDialog" persistent scrollable max-width="700")
  yz-card
    yz-dialog-normal-header(title="Unit of measurement conversion" @close="onClose")
    yz-dialog-normal-content.pa-6
      v-form(ref="form" @submit.prevent)
        .yz-regular-body-sm.text-neutral-500 Set your UOM's conversion settings. This allows you to convert stock from one unit to another for easier inventory management.
        //- Table header
        v-row.mt-4(no-gutters)
          v-col(cols="3")
            .yz-medium-body-xs.text-neutral-700 UOM
          v-col.pl-3(cols="3")
            .yz-medium-body-xs.text-neutral-700 Convert to
          v-col.pl-3(cols="2")
            .yz-medium-body-xs.text-neutral-700 Multiply by
          v-col.pl-3(cols="3")
            .yz-medium-body-xs.text-neutral-700 Summary
          v-col(cols="1")
        //- Conversion rows
        v-row.mt-3(v-for="(row, index) in localConversions" :key="index" no-gutters align="center")
          v-col(cols="3")
            medication-unit-of-measure-dropdown(v-if="index === 0" v-model="row.uom" :error="showErrors && !row.uom" distinct :team-hq-id="teamHqId" field-min-width="0" size="lg")
            yz-text-field(v-else :value="getRowUom(index)" size="lg" disabled, background-color="neutral-300")
          v-col.pl-3(cols="3")
            medication-unit-of-measure-dropdown(v-model="row.convert_to" :error="showErrors && !row.convert_to" distinct :team-hq-id="teamHqId" field-min-width="0" size="lg" @change="onConvertToChange(index)")
          v-col.pl-3(cols="2")
            yz-text-field(v-model="row.multiplier" placeholder="0" type="number" size="lg" :rules="rules.positive" step="1" min="1")
          v-col.pl-3(cols="3")
            .yz-regular-body-xs.text-neutral-500(v-if="row.convert_to && row.multiplier") 1 {{ getRowUom(index) }} = {{ row.multiplier }} {{ row.convert_to }}
          v-col.text-center(cols="1")
            yz-btn(v-if="index === 0 && localConversions.length > 1" outlined, icon="mdi-delete-outline", icon-color="danger-500" size="md" @click="onDeleteRow(index)")
        //- Add more button
        .mt-3
          yz-btn(label="Add more" size="sm" prepend-icon="mdi-plus" @click="onAddRow")
    yz-dialog-normal-actions(@cancel="onClose" @save="onSave" :save-label="hasBaseUomChanged ? 'Review changes' : 'Save'")


</template>

<script>
import MedicationUnitOfMeasureDropdown from '@/components/clinic/inventory/MedicationUnitOfMeasureDropdown.vue'
export default {
  components: {
    MedicationUnitOfMeasureDropdown
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    defaultUom: {
      type: String,
      default: null
    },
    conversions: {
      type: Array,
      default: () => []
    },
    teamHqId: {
      type: [Number, String],
      default: null
    },
    originalBaseUom: {
      type: String,
      default: null
    },
    currentStockOnHand: {
      type: Number,
      default: 0
    },
    companySlug: {
      type: String,
      default: null
    },
    medicationId: {
      type: [Number, String],
      default: null
    },
    apiEndpoint: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      localConversions: [],
      showErrors: false,
      rules: {
        positive: [
          v => (v !== null && v !== undefined && v !== '') || 'Required',
          v => Number.isInteger(Number(v)) || 'Must be integer',
          v => Number(v) > 0 || 'Must be greater than zero'
        ]
      }
    }
  },
  computed: {
    showDialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    hasBaseUomChanged() {
      if (!this.originalBaseUom) return false
      const validConversions = this.localConversions.filter(row => row.convert_to && row.multiplier)
      if (validConversions.length === 0) return false
      const currentBaseUom = validConversions[validConversions.length - 1].convert_to
      return currentBaseUom !== this.originalBaseUom
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.initConversions()
      }
    }
  },
  methods: {
    initConversions() {
      this.showErrors = false
      if (this.conversions.length > 0) {
        this.localConversions = this.conversions.map(c => ({ ...c }))
      } else {
        this.localConversions = [{ uom: null, convert_to: null, multiplier: null }]
      }
    },
    hasMissingUoms() {
      return this.localConversions.some((row, index) => {
        if (index === 0 && !row.uom) return true
        return !row.convert_to
      })
    },
    getRowUom(index) {
      if (index === 0) return this.localConversions[0]?.uom || '-'
      const prevRow = this.localConversions[index - 1]
      return prevRow?.convert_to || '-'
    },
    onConvertToChange(index) {
      // Update the next row's UOM if it exists (chaining)
      if (index + 1 < this.localConversions.length) {
        this.localConversions[index + 1].uom = this.localConversions[index].convert_to
      }
    },
    onAddRow() {
      const lastRow = this.localConversions[this.localConversions.length - 1]
      this.localConversions.push({
        uom: lastRow?.convert_to || null,
        convert_to: null,
        multiplier: null
      })
    },
    onDeleteRow(index) {
      this.localConversions.splice(index, 1)
      if (this.localConversions.length === 0) {
        this.localConversions.push({ uom: null, convert_to: null, multiplier: null })
      }
    },
    buildConversions() {
      return this.localConversions
        .filter(row => row.convert_to && row.multiplier)
        .map((row, index) => ({
          uom: this.getRowUom(index),
          convert_to: row.convert_to,
          multiplier: Number(row.multiplier)
        }))
    },
    onSave() {
      this.showErrors = true
      const formValid = this.$refs.form.validate()
      if (!formValid || this.hasMissingUoms()) return
      const conversions = this.buildConversions()
      if (this.hasBaseUomChanged) {
        this.$emit('base-uom-change-requested', {
          conversions,
          newBaseUom: conversions[conversions.length - 1].convert_to
        })
        return
      }
      this.$emit('save', conversions)
      this.showDialog = false
    },
    onClose() {
      this.showDialog = false
    }
  }
}
</script>
